<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Editor } from '@guolao/vue-monaco-editor';
import { UiInfoBox, UiStandardButton, UiText } from '@shopexperts/shared';
import { parseSchemaDraft, serializeSchemaDraft, type SchemaDraft } from '../../utils/schemaJson';

const props = defineProps<{
  draft: SchemaDraft;
}>();

const emit = defineEmits<{
  apply: [draft: SchemaDraft];
}>();

const text = ref(serializeSchemaDraft(props.draft));
const parsed = computed(() => parseSchemaDraft(text.value));
const errors = computed(() => parsed.value.errors);
const canApply = computed(() => !errors.value.length);

/**
 * Reload the editor whenever the form-side draft moves on its own (schema
 * loaded, another schema selected), but never while the JSON is being typed —
 * that would fight the user mid-edit.
 */
watch(() => serializeSchemaDraft(props.draft), (next, previous) => {
  if (next === previous || next === text.value) return;
  if (text.value === previous) text.value = next;
}, { flush: 'post' });

function onEditorUpdate(value: string | undefined) {
  text.value = value ?? '';
}

function formatJson() {
  const draft = parsed.value.draft;
  if (draft) text.value = serializeSchemaDraft(draft);
}

function resetFromForm() {
  text.value = serializeSchemaDraft(props.draft);
}

function applyToEditor() {
  const draft = parsed.value.draft;
  if (!draft) return;
  emit('apply', draft);
}
</script>

<template>
  <section class="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
    <div class="flex flex-wrap items-start justify-between gap-3 border-b border-se-gray-200 px-6 py-5">
      <div class="min-w-0">
        <UiText as="h2" variant="h5" text="Schema JSON" />
        <UiText
          as="p"
          variant="p-small"
          class="mt-1 text-se-gray-700"
          text="Edit the whole schema as JSON. Apply it to load the changes into the builder, then save as usual."
        />
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <UiStandardButton label="Reset" variant="ghost" size="sm" @click="resetFromForm" />
        <UiStandardButton label="Format" variant="outline" size="sm" :disabled="!canApply" @click="formatJson" />
        <UiStandardButton
          label="Apply to editor"
          icon="check"
          variant="primary"
          size="sm"
          :disabled="!canApply"
          data-testid="schema-json-apply"
          @click="applyToEditor"
        />
      </div>
    </div>

    <UiInfoBox v-if="errors.length" variant="error" class="mx-6 mt-4" data-testid="schema-json-errors">
      <div class="flex flex-col gap-1">
        <UiText v-for="message in errors" :key="message" as="p" variant="p-small" :text="message" />
      </div>
    </UiInfoBox>

    <div class="min-h-0 flex-1 p-6">
      <div class="h-full overflow-hidden rounded-md border border-se-gray-200">
        <Editor
          :value="text"
          language="json"
          theme="vs"
          height="100%"
          :options="{ automaticLayout: true, wordWrap: 'on', minimap: { enabled: false }, tabSize: 2 }"
          @update:value="onEditorUpdate"
        />
      </div>
    </div>
  </section>
</template>
